/**
 * Paired-device credential + cached device context.
 *
 * The kiosk is enrolled ONCE by an admin: in web-app's KiosksTab the admin mints
 * an enrollment/device token (shown a single time), then pastes it into this
 * device's pairing screen. We persist it in localStorage and inject it on every
 * API call as the `x-worklogix-device` header (see api.ts). It is NOT an Entra
 * bearer token — this app never talks to MSAL/Entra.
 *
 * Physical control of the tablet is the security perimeter (plan-v5 §Risks); the
 * token can be rotated/revoked from web-app, which surfaces here as REPEATED 401s
 * that clear the token and drop the device back to the pairing screen.
 *
 * DURABILITY: the token is mirrored in-memory (`tokenCache`) and treated as the
 * session source of truth; localStorage is only a persistence backing. iOS/iPadOS
 * evicts script-writable storage (localStorage) under memory pressure — most
 * likely right when a punch fires the camera — and previously a single such
 * eviction dropped a live pairing to the pairing screen. The in-memory mirror
 * keeps the pairing alive across a transient eviction (only a full page reload
 * with the store empty re-pairs), and all writes are best-effort (a throwing
 * setItem never fails pairing).
 */

const TOKEN_KEY = 'worklogix.kiosk.deviceToken';
const CONTEXT_KEY = 'worklogix.kiosk.context';

/** HTTP header carrying the device token. Matches Dev-B's KioskAuthGuard. */
export const DEVICE_HEADER = 'x-worklogix-device';

/**
 * In-memory mirror of the paired token — the session source of truth. Once set
 * (paired this session or hydrated from storage), it survives a transient
 * localStorage eviction so the app does not fall back to pairing mid-shift.
 */
let tokenCache: string | null = null;

/** Cached identity of the department this device is bound to. */
export interface DeviceContext {
  departmentId: string;
  departmentName: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribe to token changes (pair / clear). Returns an unsubscribe fn. */
export function subscribeDeviceToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Read the persisted token, tolerating a storage that throws (returns null). */
function readStoredToken(): string | null {
  try {
    const stored = window.localStorage.getItem(TOKEN_KEY);
    return stored !== null && stored.trim() !== '' ? stored : null;
  } catch {
    return null;
  }
}

export function getDeviceToken(): string | null {
  // Prefer the in-memory mirror: it is stable across a transient localStorage
  // eviction. Only fall back to storage when the mirror is empty (first read
  // after load / not yet paired this session), re-hydrating the mirror from it.
  if (tokenCache !== null) {
    return tokenCache;
  }
  tokenCache = readStoredToken();
  return tokenCache;
}

export function setDeviceToken(token: string): void {
  const trimmed = token.trim();
  // Memory first so pairing succeeds even if the storage write throws.
  tokenCache = trimmed;
  try {
    window.localStorage.setItem(TOKEN_KEY, trimmed);
  } catch {
    // Best-effort persistence — the in-memory mirror still holds the pairing.
  }
  emit();
}

/** Clear the token (e.g. on a confirmed revoke) and return the device to pairing. */
export function clearDeviceToken(): void {
  tokenCache = null;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(CONTEXT_KEY);
  } catch {
    // Ignore — the in-memory mirror is already cleared, which is what gates the UI.
  }
  emit();
}

export function getDeviceContext(): DeviceContext | null {
  try {
    const raw = window.localStorage.getItem(CONTEXT_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<DeviceContext>;
    if (typeof parsed.departmentId === 'string' && typeof parsed.departmentName === 'string') {
      return { departmentId: parsed.departmentId, departmentName: parsed.departmentName };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Cache the department identity learned from `GET /kiosk/current`. Lets the
 * banner render and lets queued offline punches be tagged with their department
 * for the local projection before the next server round-trip.
 */
export function setDeviceContext(context: DeviceContext): void {
  try {
    window.localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Best-effort cache — the banner falls back to the server's next response.
  }
}
